/**
 * Supabase clients grouped by module.
 * `supabase` remains the app-core default for auth/tasks/jobs/monitor-state widgets.
 * Live odds and props widgets should import their dedicated clients explicitly.
 */

import { createClient } from '@supabase/supabase-js'

function trimUrl(value) {
  return typeof value === 'string' ? value.replace(/\/+$/, '') : ''
}

function createSupabaseInstance(url, key) {
  return (url && key) ? createClient(url, key) : null
}

const APP_CORE_URL = trimUrl(import.meta.env.VITE_APP_CORE_SUPABASE_URL || import.meta.env.VITE_SUPABASE_URL)
const APP_CORE_KEY = import.meta.env.VITE_APP_CORE_SUPABASE_ANON_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY

const LIVE_ODDS_URL = trimUrl(import.meta.env.VITE_LIVE_ODDS_SUPABASE_URL)
const LIVE_ODDS_KEY = import.meta.env.VITE_LIVE_ODDS_SUPABASE_ANON_KEY

const MLB_MONITORS_URL = trimUrl(import.meta.env.VITE_MLB_MONITORS_SUPABASE_URL)
const MLB_MONITORS_KEY = import.meta.env.VITE_MLB_MONITORS_SUPABASE_ANON_KEY

const PROPS_URL = trimUrl(import.meta.env.VITE_PROPS_SUPABASE_URL)
const PROPS_KEY = import.meta.env.VITE_PROPS_SUPABASE_ANON_KEY

export const appCoreSupabase = createSupabaseInstance(APP_CORE_URL, APP_CORE_KEY)
export const liveOddsSupabase = createSupabaseInstance(LIVE_ODDS_URL, LIVE_ODDS_KEY)
export const mlbMonitorsSupabase = createSupabaseInstance(MLB_MONITORS_URL, MLB_MONITORS_KEY)
export const propsSupabase = createSupabaseInstance(PROPS_URL, PROPS_KEY)

// Backward-compatible default: low-write control-plane DB.
export const supabase = appCoreSupabase

const clientConfigs = {
  appCore: { url: APP_CORE_URL, key: APP_CORE_KEY },
  liveOdds: { url: LIVE_ODDS_URL, key: LIVE_ODDS_KEY },
  mlbMonitors: { url: MLB_MONITORS_URL, key: MLB_MONITORS_KEY },
  props: { url: PROPS_URL, key: PROPS_KEY },
}

/**
 * Check if Supabase is reachable. Returns { ok: boolean, error?: string }
 * Caches result for 30 seconds to avoid hammering a down service.
 */
let _healthCache = {
  appCore: { ok: true, checkedAt: 0 },
  liveOdds: { ok: true, checkedAt: 0 },
  mlbMonitors: { ok: true, checkedAt: 0 },
  props: { ok: true, checkedAt: 0 },
}
const HEALTH_CACHE_TTL = 30000

export async function checkSupabaseHealth(target = 'appCore') {
  const config = clientConfigs[target] || clientConfigs.appCore
  const now = Date.now()
  const cached = _healthCache[target] || { ok: true, checkedAt: 0 }
  if (now - cached.checkedAt < HEALTH_CACHE_TTL) return cached

  if (!config.url || !config.key) {
    _healthCache[target] = { ok: false, error: 'Supabase not configured', checkedAt: now }
    return _healthCache[target]
  }

  try {
    const resp = await fetch(`${config.url}/rest/v1/`, {
      method: 'HEAD',
      headers: { apikey: config.key },
      signal: AbortSignal.timeout(5000),
    })
    const ok = resp.ok
    _healthCache[target] = { ok, error: ok ? null : `HTTP ${resp.status}`, checkedAt: now }
  } catch (e) {
    _healthCache[target] = { ok: false, error: e.message || 'Connection failed', checkedAt: now }
  }
  return _healthCache[target]
}

export function isSupabaseConfigured(client = supabase) {
  return !!client
}

/**
 * Detect if an error message is actually an HTML response (e.g. Cloudflare 522)
 * instead of a proper JSON error from Supabase.
 */
export function isHtmlError(msg) {
  if (!msg || typeof msg !== 'string') return false
  const lower = msg.toLowerCase()
  return lower.includes('<html') || lower.includes('doctype') || lower.includes('<!doctype')
}

export const SUPABASE_DOWN_MESSAGE =
  'Database temporarily unavailable. The Supabase instance may be paused \u2014 please check the Supabase dashboard.'
